import { endpoints, type LeadCreate, type LeadUpdate } from '@gopinhos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../lib/api-client';

export interface LeadSummary {
  id: string;
  projectId: string;
  flowchartId: string;
  currentStageId: string;
  title: string;
  contactName: string | null;
  contactEmail: string | null;
  company: string | null;
  valueCents: number | null;
  currency: string | null;
  assignedToUserId: string | null;
  enteredStageAt: string;
  updatedAt: string;
}

export function useLeads(projectId: string | undefined) {
  return useQuery({
    queryKey: ['leads', projectId],
    enabled: !!projectId,
    queryFn: () =>
      apiClient.request<unknown, never>({
        method: 'GET',
        path: endpoints.projectLeads(projectId!),
      }) as Promise<{ items: LeadSummary[]; nextCursor: string | null }>,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    enabled: !!id,
    queryFn: () =>
      apiClient.request<unknown, never>({
        method: 'GET',
        path: endpoints.lead(id!),
      }),
  });
}

export function useCreateLead(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadCreate) =>
      apiClient.request({
        method: 'POST',
        path: endpoints.projectLeads(projectId),
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads', projectId] });
    },
  });
}

export function useUpdateLead(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeadUpdate) =>
      apiClient.request({
        method: 'PATCH',
        path: endpoints.lead(id),
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead', id] });
    },
  });
}

export function useMoveLead(leadId: string, projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { transitionId: string; note?: string; idempotencyKey?: string }) =>
      apiClient.request({
        method: 'POST',
        path: endpoints.leadMove(leadId),
        body: { transitionId: input.transitionId, note: input.note },
        idempotencyKey: input.idempotencyKey ?? crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads', projectId] });
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });
}
